import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/data";
import { scrollToSection } from "@/lib/scroll";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

const WHATSAPP_URL = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(
  "Hi JM Investment Advisory, I'd like to know more about your services."
)}`;

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection(target), 400);
    } else {
      scrollToSection(target);
    }
  };

  const goRoute = (route) => {
    setOpen(false);
    navigate(route);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
          scrolled
            ? "bg-[#FAFAF8]/80 backdrop-blur-xl border-b border-[#E2E8F0] py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between">
          <button
            onClick={() => go("hero")}
            className="flex items-center gap-3 group"
            data-testid="navbar-logo"
          >
            <img src={scrolled ? BRAND.logo : BRAND.logoLight} alt="JM Investment Advisory" className="h-10 w-auto object-contain" />
            <span
              className={`font-trajan text-[0.8rem] sm:text-lg font-medium uppercase tracking-[0.12em] sm:tracking-[0.18em] leading-none block transition-colors duration-500 ${
                scrolled ? "text-[#0F1E3D]" : "text-[#FAFAF8]"
              }`}
            >
              JM Investment Advisory
            </span>
          </button>

          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((link) => {
              const key = link.route || link.target;
              const testid = `nav-${(link.route || link.target).replace(/[/]/g, "")}`;
              const cls = `nav-link text-sm font-semibold tracking-wide transition-colors duration-500 ${
                scrolled
                  ? "text-[#0F1E3D] hover:text-[#C89933]"
                  : "text-[#FAFAF8] hover:text-[#C89933]"
              }`;
              return link.route ? (
                <Link key={key} to={link.route} className={cls} data-testid={testid} onClick={() => setOpen(false)}>
                  {link.label}
                </Link>
              ) : (
                <button key={key} onClick={() => go(link.target)} className={cls} data-testid={testid}>
                  {link.label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Chat with us on WhatsApp"
              data-testid="navbar-whatsapp"
              className={`hidden sm:flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-400 hover:bg-[#25D366] hover:border-[#25D366] hover:text-white ${
                scrolled
                  ? "border-[#0F1E3D]/20 text-[#0F1E3D]"
                  : "border-white/40 text-[#FAFAF8]"
              }`}
            >
              <WhatsAppIcon className="h-[18px] w-[18px]" />
            </a>
            <button
              onClick={() => go("contact")}
              className={`hidden sm:inline-flex items-center rounded-full px-6 py-2.5 text-sm font-medium transition-colors duration-400 ${
                scrolled
                  ? "bg-[#0F1E3D] text-[#FAFAF8] hover:bg-[#C89933] hover:text-[#0F1E3D]"
                  : "bg-[#C89933] text-[#0F1E3D] hover:bg-[#FAFAF8]"
              }`}
              data-testid="navbar-book-btn"
            >
              Book a Consultation
            </button>
            <button
              onClick={() => setOpen(true)}
              className={`md:hidden transition-colors duration-500 ${scrolled ? "text-[#0F1E3D]" : "text-[#FAFAF8]"}`}
              data-testid="navbar-menu-open"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#0F1E3D] text-[#FAFAF8] flex flex-col md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center gap-3 px-6 py-5 border-b border-white/10">
              <img src={BRAND.logoLight} alt="JM Investment Advisory" className="h-9 w-auto object-contain" />
              <span className="font-trajan text-sm font-medium uppercase tracking-[0.18em]">JM Investment Advisory</span>
              <button onClick={() => setOpen(false)} data-testid="navbar-menu-close" aria-label="Close menu" className="ml-auto">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-2 px-6">
              {NAV_LINKS.map((link, i) => {
                const key = link.route || link.target;
                const testid = `mobile-nav-${(link.route || link.target).replace(/[/]/g, "")}`;
                const cls = "text-left font-serif-display text-4xl py-3 border-b border-white/10";
                return (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.07 }}
                  >
                    {link.route ? (
                      <Link to={link.route} onClick={() => setOpen(false)} className={`${cls} block`} data-testid={testid}>
                        {link.label}
                      </Link>
                    ) : (
                      <button onClick={() => go(link.target)} className={`${cls} w-full`} data-testid={testid}>
                        {link.label}
                      </button>
                    )}
                  </motion.div>
                );
              })}
              <button
                onClick={() => go("contact")}
                className="mt-8 rounded-full bg-[#C89933] px-6 py-4 text-base font-medium text-[#0F1E3D]"
                data-testid="mobile-book-btn"
              >
                Book a Consultation
              </button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="mt-4 flex items-center justify-center gap-2.5 rounded-full border border-[#25D366] px-6 py-4 text-base font-medium text-[#25D366]"
                data-testid="mobile-whatsapp"
              >
                <WhatsAppIcon className="h-5 w-5" />
                Chat on WhatsApp
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
